// POS Shift helper — open / close a counter shift and compute its totals.
//
// A shift is one cashier session on one counter. No POS sale is allowed unless
// a shift is open on that counter. Closing a shift counts the drawer, compares
// against the system-expected cash, records the variance and prints a Z-report.
//
// Cash is NOT moved on close (locked decision): each counter's cash account is a
// permanent GL account. The admin transfers to main Cash-in-Hand via a manual JV
// whenever they choose. So close = count + variance report only.
//
// Shift management requires connectivity (Firestore). Selling offline is fine
// once a shift is already open (the open shift is loaded into POSScreen state).

import {
  collection, doc, addDoc, updateDoc, getDocs, query, where, runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ─── Z (shift) number — per-counter continuous series ───────────────────────
const generateShiftNumber = async (companyId, counter) => {
  const code = counter?.counterCode || 'POS';
  const counterKey = `Z__${counter.id}`;
  const counterRef = doc(db, 'companies', companyId, 'documentCounters', counterKey);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? (Number(snap.data().current) || 0) : 0;
    const next = current + 1;
    tx.set(counterRef, { prefix: `Z-${code}-`, current: next, updatedAt: new Date().toISOString() });
    return `Z-${code}-${String(next).padStart(5, '0')}`;
  });
};

// ─── Local cache of the open shift (offline resilience) ─────────────────────
// getOpenShift needs Firestore. Caching the open shift lets the POS keep selling
// after a page reload while offline (selling offline is fine once a shift is
// open; only open/close need connectivity).
const shiftCacheKey = (companyId, counterId) => `amal.pos.openShift.${companyId}.${counterId}`;

export const cacheOpenShift = (companyId, counterId, shift) => {
  if (!companyId || !counterId) return;
  try {
    if (shift) localStorage.setItem(shiftCacheKey(companyId, counterId), JSON.stringify(shift));
    else localStorage.removeItem(shiftCacheKey(companyId, counterId));
  } catch { /* quota — ignore */ }
};

export const readCachedOpenShift = (companyId, counterId) => {
  if (!companyId || !counterId) return null;
  try {
    const raw = localStorage.getItem(shiftCacheKey(companyId, counterId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// ─── Find the currently-open shift for a counter (or null) ──────────────────
export const getOpenShift = async (companyId, counterId) => {
  if (!companyId || !counterId) return null;
  const shiftsRef = collection(db, 'companies', companyId, 'posShifts');
  const q = query(shiftsRef, where('counterId', '==', counterId), where('status', '==', 'open'));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // If more than one somehow exists, take the most recently opened.
  const shifts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  shifts.sort((a, b) => String(b.openedAt || '').localeCompare(String(a.openedAt || '')));
  return shifts[0];
};

// ─── GL balance of the counter's cash account (the real opening float) ──────
// Cash is an asset → balance = Σ(debit − credit) over its ledger. Mirrors the
// Customer/Vendor Balances math: Posted entries + Opening-Balance drafts.
export const getCashAccountBalance = async (companyId, accountId) => {
  if (!companyId || !accountId) return 0;
  const ref = collection(db, 'companies', companyId, 'subTransactions');
  const snap = await getDocs(query(ref, where('accountId', '==', accountId)));
  // ACTIVE FINANCIAL YEAR ONLY. Opening balances are entered per year and the new
  // year's opening already contains last year's closing — summing both doubled the
  // cash balance (and therefore the POS opening float). Rows with no year still count.
  const fy = (typeof window !== 'undefined' && window.__AMAL_ACTIVE_FY_ID__) || null;
  let bal = 0;
  snap.docs.forEach(d => {
    const t = d.data();
    const include = t.status === 'Posted' || (t.status === 'Draft' && t.tranTypeCode === 'OB');
    if (!include) return;
    if (fy && t.financialYearId && t.financialYearId !== fy) return;
    bal += (Number(t.debitAmount) || 0) - (Number(t.creditAmount) || 0);
  });
  return Math.round(bal * 100) / 100;
};

// ─── Open a shift ───────────────────────────────────────────────────────────
export const openShift = async (companyId, counter, user, openingCash) => {
  if (!companyId || !counter?.id) throw new Error('Counter is required to open a shift.');
  const existing = await getOpenShift(companyId, counter.id);
  if (existing) return existing; // already open — reuse

  const shiftNumber = await generateShiftNumber(companyId, counter);
  const now = new Date().toISOString();
  const payload = {
    shiftNumber,
    counterId: counter.id,
    counterCode: counter.counterCode || null,
    counterName: counter.counterName || null,
    branchId: counter.branchId || null,
    branchName: counter.branchName || null,
    cashAccountId: counter.cashAccountId || null,
    cashAccountCode: counter.cashAccountCode || null,
    cashAccountName: counter.cashAccountName || null,
    openedByUserId: user?.uid || null,
    openedByName: user?.displayName || user?.fullName || user?.name || 'Cashier',
    openedAt: now,
    openingCash: Number(openingCash) || 0,
    status: 'open',
    createdAt: now
  };
  const ref = await addDoc(collection(db, 'companies', companyId, 'posShifts'), payload);
  return { id: ref.id, ...payload };
};

// ─── Sum a shift's sales/returns from the transactions it stamped ───────────
// Drawer cash = opening + cash sales − cash refunds. Card/credit don't touch it.
export const getShiftTotals = async (companyId, shiftId) => {
  const out = {
    saleCount: 0, returnCount: 0,
    cashSales: 0, cardSales: 0, creditSales: 0,
    cashReturns: 0, cardReturns: 0, creditReturns: 0,
    grossSales: 0, grossReturns: 0
  };
  if (!companyId || !shiftId) return out;

  const txRef = collection(db, 'companies', companyId, 'transactions');
  const snap = await getDocs(query(txRef, where('shiftId', '==', shiftId)));

  snap.docs.forEach(d => {
    const t = d.data();
    if (t.status && t.status !== 'Posted') return; // skip voided/cancelled
    const isReturn = t.tranTypeCode === 'SR';
    const payments = Array.isArray(t.posPayments) ? t.posPayments : [];
    if (isReturn) {
      out.returnCount += 1;
      out.grossReturns += Number(t.totalAmount) || 0;
    } else {
      out.saleCount += 1;
      out.grossSales += Number(t.totalAmount) || 0;
    }
    payments.forEach(p => {
      const amt = Number(p.amount) || 0;
      const method = (p.method || 'cash').toLowerCase();
      const bucket = method === 'cash' ? 'cash' : (method === 'credit' ? 'credit' : 'card');
      if (isReturn) out[`${bucket}Returns`] += amt;
      else out[`${bucket}Sales`] += amt;
    });
  });
  return out;
};

// ─── Close a shift ──────────────────────────────────────────────────────────
export const closeShift = async (companyId, shift, { countedCash, notes }, user) => {
  if (!companyId || !shift?.id) throw new Error('Shift is required to close.');
  const totals = await getShiftTotals(companyId, shift.id);
  const opening = Number(shift.openingCash) || 0;
  const expectedCash = Math.round((opening + totals.cashSales - totals.cashReturns) * 100) / 100;
  const counted = Number(countedCash) || 0;
  const variance = Math.round((counted - expectedCash) * 100) / 100;
  const now = new Date().toISOString();

  const closeData = {
    status: 'closed',
    closedByUserId: user?.uid || null,
    closedByName: user?.displayName || user?.fullName || user?.name || 'Cashier',
    closedAt: now,
    saleCount: totals.saleCount,
    returnCount: totals.returnCount,
    systemCashSales: totals.cashSales,
    systemCardSales: totals.cardSales,
    systemCreditSales: totals.creditSales,
    systemCashReturns: totals.cashReturns,
    systemCardReturns: totals.cardReturns,
    systemCreditReturns: totals.creditReturns,
    grossSales: totals.grossSales,
    grossReturns: totals.grossReturns,
    expectedCash,
    countedCash: counted,
    variance,
    notes: notes || '',
    updatedAt: now
  };
  await updateDoc(doc(db, 'companies', companyId, 'posShifts', shift.id), closeData);
  return { ...shift, ...closeData, ...totals, expectedCash, variance };
};

